import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { css } from '@emotion/core';
import {
  Tabs,
  Text,
  Heading,
  PrimaryButton,
  SecondaryButton,
} from '@weave/design-system';
import { theme } from '@weave/theme';
import { Page } from '../shared';
import { RegisteredServicesTab } from './registered-services-tab';
import { MigrationsTab } from './migrations-tab';
import { retryMigration, rollbackMigration } from './api';
import { useAlert } from '@weave/alert-system';

const modalOverlayStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const modalContentStyles = css`
  background: white;
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(4)};
  max-width: 500px;
  width: 90%;
  box-shadow: ${theme.shadows.heavy};
`;

const modalActionsStyles = css`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing(2)};
  margin-top: ${theme.spacing(4)};
`;

export const OrgMergePage = () => {
  const alert = useAlert();
  const [currentTab, setCurrentTab] = useState<string>('migrations');
  const [isRetrying, setIsRetrying] = useState(false);
  const [migrationToRetry, setMigrationToRetry] = useState<{
    id: string;
    isRollbackFailed: boolean;
  } | null>(null);
  const [retryRefetchTrigger, setRetryRefetchTrigger] = useState(0);
  const [showRetryModal, setShowRetryModal] = useState(false);

  const [isRollingBack, setIsRollingBack] = useState(false);
  const [migrationToRollback, setMigrationToRollback] = useState<string | null>(null);
  const [rollbackRefetchTrigger, setRollbackRefetchTrigger] = useState(0);
  const [showRollbackModal, setShowRollbackModal] = useState(false);

  const handleRetryClick = (migrationId: string, isRollbackFailed: boolean) => {
    setMigrationToRetry({ id: migrationId, isRollbackFailed });
    setShowRetryModal(true);
  };

  const handleRetryModalClose = () => {
    if (isRetrying) return;
    setShowRetryModal(false);
    setMigrationToRetry(null);
  };

  const handleRetryConfirm = async () => {
    if (!migrationToRetry) return;

    setIsRetrying(true);

    try {
      await retryMigration(migrationToRetry.id);
      setShowRetryModal(false);
      setMigrationToRetry(null);
      setRetryRefetchTrigger((prev) => prev + 1);
      alert.success('Migration retry started successfully');
    } catch (error) {
      console.error('Error retrying migration:', error);
      alert.error('Failed to retry migration');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRollbackClick = (migrationId: string) => {
    setMigrationToRollback(migrationId);
    setShowRollbackModal(true);
  };

  const handleRollbackModalClose = () => {
    if (isRollingBack) return;
    setShowRollbackModal(false);
    setMigrationToRollback(null);
  };

  const handleRollbackConfirm = async () => {
    if (!migrationToRollback) return;

    setIsRollingBack(true);

    try {
      await rollbackMigration(migrationToRollback);
      setShowRollbackModal(false);
      setMigrationToRollback(null);
      setRollbackRefetchTrigger((prev) => prev + 1);
      alert.success('Migration rollback started successfully');
    } catch (error) {
      console.error('Error rolling back migration:', error);
      alert.error('Failed to rollback migration');
    } finally {
      setIsRollingBack(false);
    }
  };

  return (
    <>
      <Page title="Organization Merge" showBackBtn={false}>
        <Tabs initialTab="migrations" onChange={setCurrentTab}>
          <Tabs.Bar>
            <Tabs.Tab id="migrations" controls="migrations-panel">
              Migrations
            </Tabs.Tab>
            <Tabs.Tab id="registered-services" controls="registered-services-panel">
              Registered Services
            </Tabs.Tab>
          </Tabs.Bar>

          {currentTab === 'migrations' && (
            <Tabs.Panel id="migrations-panel" controller="migrations">
              <MigrationsTab
                onRetryClick={handleRetryClick}
                retryRefetchTrigger={retryRefetchTrigger}
                onRollbackClick={handleRollbackClick}
                rollbackRefetchTrigger={rollbackRefetchTrigger}
              />
            </Tabs.Panel>
          )}

          {currentTab === 'registered-services' && (
            <Tabs.Panel id="registered-services-panel" controller="registered-services">
              <RegisteredServicesTab />
            </Tabs.Panel>
          )}
        </Tabs>
      </Page>

      {/* Using custom portal modals below as a workaround for a bug in the DS Modal component
          where it conflicts with the Page layout's z-index/stacking context. */}
      {showRetryModal &&
        ReactDOM.createPortal(
          <div css={modalOverlayStyles} onClick={handleRetryModalClose}>
            <div css={modalContentStyles} onClick={(e) => e.stopPropagation()}>
              <Heading
                level={2}
                css={css`
                  margin-bottom: ${theme.spacing(2)};
                `}
              >
                {migrationToRetry?.isRollbackFailed
                  ? 'Retry Rollback'
                  : 'Retry Migration'}
              </Heading>
              <Text>
                {migrationToRetry?.isRollbackFailed
                  ? 'This will retry all failed services in the rollback. Unless the original failure was a transitory issue, a retry is unlikely to help without code changes to the failed service.'
                  : 'This will retry all failed services in the migration. Unless the original failure was a transitory issue, a retry is unlikely to help without code changes to the failed service.'}
              </Text>
              <div css={modalActionsStyles}>
                <SecondaryButton onClick={handleRetryModalClose} disabled={isRetrying}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleRetryConfirm} disabled={isRetrying}>
                  {isRetrying
                    ? 'Retrying...'
                    : migrationToRetry?.isRollbackFailed
                    ? 'Retry Rollback'
                    : 'Retry Migration'}
                </PrimaryButton>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showRollbackModal &&
        ReactDOM.createPortal(
          <div css={modalOverlayStyles} onClick={handleRollbackModalClose}>
            <div css={modalContentStyles} onClick={(e) => e.stopPropagation()}>
              <Heading
                level={2}
                css={css`
                  margin-bottom: ${theme.spacing(2)};
                `}
              >
                Rollback Migration
              </Heading>
              <Text>
                This will largely revert both organizations to their state before the
                merge. However, assets, including their location associations, will not be
                affected.
              </Text>
              <div css={modalActionsStyles}>
                <SecondaryButton
                  onClick={handleRollbackModalClose}
                  disabled={isRollingBack}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  onClick={handleRollbackConfirm}
                  disabled={isRollingBack}
                  css={css`
                    background-color: ${theme.colors.critical10};
                    color: ${theme.colors.critical50};
                    border: 1px solid ${theme.colors.critical50};
                    &:hover:not(:disabled) {
                      background-color: ${theme.colors.critical20};
                    }
                  `}
                >
                  {isRollingBack ? 'Rolling Back...' : 'Rollback Migration'}
                </PrimaryButton>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
